import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";

const ChapterView = () => {
  const bgNavajoBand = {
    backgroundImage: `url(/uploads/Navajo-band-color.png)`,
    // backgroundImage: `url(${process.env.PUBLIC_URL}/uploads/Navajo-band-color.png)`,
    backgroundPosition: "center top",
    backgroundRepeat: "repeat-x",
    backgroundSize: "auto",
    paddingTop: "150px",
    paddingBottom: "80px",
  };
  const { id } = useParams();
  const navigate = useNavigate();
  const [chapter, setChapter] = useState({
    title: "",
    content: "",
    image_path: "",
    action1_text: "",
    action1_next_chapter: null,
    action2_text: "",
    action2_next_chapter: null,
    clues: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clueAnswers, setClueAnswers] = useState({});
  const [clueError, setClueError] = useState("");
  const [cluesVerified, setCluesVerified] = useState(false);

  useEffect(() => {
    const fetchChapter = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/api/chapters/${id}`);
        const data = response.data;

        // Parse clues from JSON string if it's a string
        const clues =
          typeof data.clues === "string"
            ? JSON.parse(data.clues)
            : Array.isArray(data.clues)
            ? data.clues
            : [];

        setChapter({ ...data, clues });

        // Initialize clue answers state
        const initialAnswers = {};
        clues.forEach((clue, index) => {
          initialAnswers[index] = "";
        });
        setClueAnswers(initialAnswers);

        // Reset verification state when chapter changes
        setCluesVerified(false);
      } catch (err) {
        setError("Error loading chapter");
        console.error("Error fetching chapter:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchChapter();
  }, [id]);

  const handleClueAnswerChange = (index, value) => {
    setClueAnswers((prev) => ({
      ...prev,
      [index]: value,
    }));
  };

  const handleClueSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`/api/chapters/${id}/verify-clues`, {
        answers: Object.values(clueAnswers),
      });

      if (response.data.success) {
        setCluesVerified(true);
        setClueError("");
      } else {
        setClueError("Incorrect answers. Please try again.");
      }
    } catch (err) {
      setClueError("Error verifying clues");
      console.error("Error verifying clues:", err);
    }
  };

  const handleAction = (nextChapterId) => {
    if (nextChapterId) {
      navigate(`/chapter/${nextChapterId}`);
    }
  };

  if (loading)
    return (
      <div className="flex items-center justify-center min-h-screen bg-sacred-white">
        Loading...
      </div>
    );
  if (error)
    return (
      <div className="text-earth-red text-center p-4 bg-sacred-white">
        Error: {error}
      </div>
    );
  if (!chapter)
    return (
      <div className="text-center p-4 bg-sacred-white">Chapter not found</div>
    );

  // Show clue form if there are clues and they haven't been verified
  if (chapter.clues && chapter.clues.length > 0 && !cluesVerified) {
    return (
      <div className="container mx-auto p-4">
        <div className="flex flex-col lg:flex-row gap-8">
          <div className="lg:w-1/2">
            <h1 className="text-2xl font-bold mb-4">
              Chapter {id} - Clue Verification
            </h1>
            <p className="mb-4 text-gray-600">
              Please answer the following clues to access this chapter:
            </p>
            <form onSubmit={handleClueSubmit} className="space-y-4">
              {chapter.clues.map((clue, index) => (
                <div key={index} className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {clue.question}
                  </label>
                  <input
                    type="text"
                    value={clueAnswers[index] || ""}
                    onChange={(e) =>
                      handleClueAnswerChange(index, e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                  />
                </div>
              ))}
              {clueError && (
                <div className="text-red-500 mb-4">{clueError}</div>
              )}
              <button
                type="submit"
                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
              >
                Submit Answers
              </button>
            </form>
          </div>
          <div className="lg:w-1/2">
            <div className="sticky top-8">
              <img
                src={`/uploads/Navajo-Night-Sky-Painterly.jpg`}
                alt="night sky with a full moon on the Navajo Reservation"
                className="w-full h-auto rounded-2xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show chapter content only after clues are verified or if there are no clues
  return (
    <div
      style={bgNavajoBand}
      className="min-h-screen bg-sacred-white py-8 px-4 sm:px-6 lg:px-8"
    >
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-semibold text-obsidian-black">
            {chapter.title}
          </h1>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          <div className="lg:w-1/2">
            <div className="prose max-w-none">
              <div
                className="text-lg text-obsidian-black"
                dangerouslySetInnerHTML={{ __html: chapter.content }}
              />
            </div>

            <div className="mt-8 space-y-4">
              {chapter.action1_text && (
                <button
                  onClick={() => handleAction(chapter.action1_next_chapter)}
                  className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-sacred-white bg-turquoise-green hover:bg-sky-blue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-turquoise-green"
                >
                  {chapter.action1_text}
                </button>
              )}

              {chapter.action2_text && (
                <button
                  onClick={() => handleAction(chapter.action2_next_chapter)}
                  className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-sacred-white bg-amber-500 hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500"
                >
                  {chapter.action2_text}
                </button>
              )}
            </div>
          </div>

          {chapter.image_path && (
            <div className="lg:w-1/2">
              <div className="sticky top-8">
                <img
                  src={`/uploads/${chapter.image_path}`}
                  alt={chapter.title}
                  className="w-full h-auto rounded-2xl shadow-lg"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChapterView;
